import * as Sentry from "@sentry/react";

/**
 * Sentry DSN — hardcoded because DSNs are public by design (they're embedded
 * in every shipped bundle anyway, anyone with devtools can read them). Pasting
 * it here means Lovable doesn't need a build-time env var, which it doesn't
 * expose cleanly.
 *
 * If you want to disable Sentry (e.g. for a local dev clone), set
 * VITE_SENTRY_DSN to the literal string "off".
 */
const HARDCODED_DSN =
  "https://4c0abb75564deef27d183a48c2d69433@o4511298781446145.ingest.us.sentry.io/4511299406266368";

/**
 * Initialize Sentry. The backend mirrors this with a SENTRY_DSN-gated init
 * in backend/main.py:24-51, also with sendDefaultPii=False.
 *
 * Why sendDefaultPii is false: Sentry's default sends IPs, cookies, and
 * request headers — for PREP that means merchant emails / phone numbers /
 * JWT tokens could leak into Sentry. We mirror the backend setting here for
 * TCPA / PCI blast-radius reasons.
 */
export function initSentry(): void {
  const envDsn = import.meta.env.VITE_SENTRY_DSN;
  if (envDsn === "off") return;
  const dsn = envDsn || HARDCODED_DSN;
  if (!dsn) return;

  const propagationTargets: (string | RegExp)[] = ["localhost"];
  const apiUrl = import.meta.env.VITE_API_URL;
  if (apiUrl) {
    try {
      propagationTargets.push(new URL(apiUrl).host);
    } catch {
      // VITE_API_URL malformed — skip remote propagation rather than crash init
    }
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    sendDefaultPii: false,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: Number.parseFloat(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? "1.0",
    ),
    tracePropagationTargets: propagationTargets,
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      "Non-Error promise rejection captured",
    ],
    beforeSend(event) {
      // Strip query strings — they often carry auth tokens or merchant prompts
      // (e.g. /app/insights?prompt=..., OAuth callback ?code=..., reset ?token=...)
      if (event.request?.url) {
        try {
          const u = new URL(event.request.url);
          u.search = "";
          event.request.url = u.toString();
        } catch {
          // ignore — leave the URL as-is if it can't be parsed
        }
      }
      return event;
    },
  });
}
